'use client'

import {
  AnimatePresence,
  type ForwardRefComponent,
  type HTMLMotionProps,
  motion,
} from 'framer-motion'
import type * as React from 'react'
import { animationVariants } from '../../utils/animations'

type MotionTag =
  | 'div'
  | 'section'
  | 'article'
  | 'aside'
  | 'main'
  | 'header'
  | 'footer'
  | 'span'
  | 'ul'
  | 'li'
  | 'tbody'
  | 'tr'

function getMotionComponent(as: MotionTag) {
  const elements = motion as unknown as Record<
    string,
    ForwardRefComponent<HTMLElement, HTMLMotionProps<'div'>>
  >
  return elements[as] ?? motion.div
}

/**
 * Standardized Page Transition wrapper.
 */
export function PageTransition({
  children,
  className,
  as = 'div',
  ...props
}: HTMLMotionProps<'div'> & { as?: MotionTag }) {
  const Component = getMotionComponent(as)

  return (
    <Component
      variants={animationVariants.fadeIn}
      initial="initial"
      animate="animate"
      exit="exit"
      className={className}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * AnimateIn wraps content to animate it as it enters the viewport.
 */
export function AnimateIn({
  children,
  variant = 'slideUp',
  delay = 0,
  className,
  as = 'div',
  ...props
}: {
  children?: React.ReactNode
  variant?: keyof typeof animationVariants
  delay?: number
  className?: string
  as?: MotionTag
} & HTMLMotionProps<'div'>) {
  const selectedVariant = animationVariants[variant] || animationVariants.slideUp
  const Component = getMotionComponent(as)

  return (
    <Component
      variants={selectedVariant}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: '-40px' }}
      transition={{ delay }}
      className={className}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * StaggerChildren helps animate lists of items.
 */
export function StaggerChildren({
  children,
  className,
  as = 'div',
  ...props
}: HTMLMotionProps<'div'> & { as?: MotionTag }) {
  const Component = getMotionComponent(as)

  return (
    <Component
      variants={animationVariants.staggerContainer}
      initial="initial"
      animate="animate"
      className={className}
      {...props}
    >
      {children}
    </Component>
  )
}

export { AnimatePresence }
